<template>
  <v-row>
    <line-chart
      v-if="!loadingChart"
      style="position: relative; width:98%; height:35vh"
      :chart-data="experienceData"
      :options="experienceOptions"
    />
    <log-dialog
      :headers="headers"
      :logData="logData"
      :showDialog="showLog"
      :title="dialogTitle"
      :isLoading="loading"
      :csvFilename="csvFilename"
      :courseId="getCourseId"
      @closeDialogEvent="showLog = false"
    />
  </v-row>
</template>

<script>
import LineChart from './LineChart.vue'
import LogDialog from './LogDialog.vue'
import store from '@/store/index'
import moment from 'moment'
import _ from 'lodash'
import {
  getDuration,
  interactionDetailTableHeader,
  timelineTitle,
  Multiset,
  roundToTwo,
  sortDataByDate
} from '../Constants'

export default {
  props: {
    course: {
      type: Object,
      required: false
    },
    agent: {
      type: String,
      required: false,
      default: null
    },
    agentCoursesData: {
      type: Array,
      required: false,
      default: null
    },
    agentName: {
      type: String,
      required: false,
      default: null
    },
    selectedOption: {
      type: Object,
      required: false,
      default: null
    }
  },
  components: {
    LineChart,
    LogDialog
  },
  data() {
    return {
      interactionByMonth: null,
      experienceData: {},
      experienceOptions: {},
      loading: false,
      loadingChart: false,
      headers: [],
      logData: [],
      showLog: false,
      dialogTitle: '',
      csvFilename: '',
      format: null,
      dStyle: '', // 'position: relative; width:98%; height:35vh',
      xAxisLabelMinWidth: 3
    }
  },
  computed: {
    getCourseId() {
      if (this.course != null) {
        return this.course.id
      }
      return null
    }
  },
  watch: {
    selectedOption: function(newVal, oldVal) {
      this.init()
    }
  },
  mounted() {
    this.init()
  },
  methods: {
    fitChart() {
      let chartCanvas = document.getElementById('line-chart')
      let chartParent = document.getElementById('chartParent')
      let maxWidth = chartCanvas.parentElement.parentElement.clientWidth
      let width = Math.max(
        this.experienceData.labels.length * this.xAxisLabelMinWidth,
        maxWidth
      )

      chartParent.style.width = width + 'px'
    },
    init() {
      this.loadingChart = true
      this.setData()
      this.fillChartData()
      this.loadingChart = false
      //this.fitChart()
    },
    findEgdeDates() {
      let interactions = []
      if (
        this.agentCoursesData != null &&
        this.agentCoursesData !== undefined
      ) {
        this.agentCoursesData.forEach(element => {
          if (element.interactions != null) {
            element.interactions.forEach(e => {
              interactions.push(Object.assign({}, e))
            })
          }
        })
      } else {
        interactions = Object.assign([], this.course.interactions)
      }

      let min,
        max = null

      interactions.forEach(element => {
        if (max == null) {
          max = element.timestamp
        } else if (moment(element.timestamp).isAfter(moment(max))) {
          max = element.timestamp
        }

        if (min == null) {
          min = element.timestamp
        } else if (moment(min).isAfter(moment(element.timestamp))) {
          min = element.timestamp
        }
      })

      if (this.selectedOption && this.selectedOption.start) {
        min = moment(Object.assign({}, this.selectedOption.start)).format('LLL')
        max = moment(Object.assign({}, this.selectedOption.end)).format('LLL')
      }

      let yearsDiff = moment(max).diff(moment(min), 'years')
      let monthsDiff = moment(max).diff(moment(min), 'months')
      let weeksDiff = moment(max).diff(moment(min), 'weeks')
      let daysDiff = moment(max).diff(moment(min), 'days')
      let hoursDiff = moment(max).diff(moment(min), 'hours')
      let minutesDiff = moment(max).diff(moment(min), 'minutes')
      let secondsDiff = moment(max).diff(moment(min), 'seconds')

      if (yearsDiff == 0) {
        if (monthsDiff == 0) {
          if (weeksDiff == 0) {
            if (daysDiff == 0) {
              if (hoursDiff == 0) {
                if (minutesDiff == 0) {
                  // return seconds
                  return {
                    set: this.buildDateSet(
                      min,
                      max,
                      daysDiff,
                      'D MMM YY',
                      'days'
                    ),
                    format: 'D MMM YY'
                  }
                } else {
                  // build minutes array
                  return {
                    set: this.buildDateSet(
                      min,
                      max,
                      daysDiff,
                      'D MMM YY',
                      'days'
                    ),
                    format: 'D MMM YY'
                  }
                }
              } else {
                // build hours array
                return {
                  set: this.buildDateSet(
                    min,
                    max,
                    hoursDiff,
                    'D MMM YY',
                    'days'
                  ),
                  format: 'D MMM YY'
                }
              }
            } else {
              // build days array
              return {
                set: this.buildDateSet(min, max, daysDiff, 'D MMM YY', 'days'),
                format: 'D MMM YY'
              }
            }
          } else {
            // build weeks array
            return {
              set: this.buildDateSet(min, max, weeksDiff, 'D MMM YY', 'weeks'),
              format: 'D MMM YY'
            }
          }
        } else {
          return {
            set: this.buildDateSet(min, max, monthsDiff, 'MMM YY', 'months'),
            format: 'MMM YY'
          }
        }
      } else {
        return {
          set: this.buildDateSet(min, max, monthsDiff, 'MMM YY', 'months'),
          format: 'MMM YY'
        }
      }
    },
    buildDateSet(min, max, dateDiff, format, dateUnit) {
      let dateSet = new Multiset()
      // 20 points is our limit
      // if (dateDiff > 20) {
      //   // do we have enough to show
      //   if (set.size > 1) {
      //   }
      // }
      const dateNeeded = dateDiff // + 2 //Math.abs(dateDiff - 10) + 5
      let minDate = moment(min) //.subtract(1, dateUnit)
      let maxDate = moment(max)
      if (
        this.selectedOption &&
        this.selectedOption.name &&
        this.selectedOption.name === 'all'
      ) {
        minDate = moment(min).subtract(1, dateUnit)
        maxDate = moment(max).add(1, dateUnit)
      }

      // if (moment(max).isSame(moment(), dateUnit)) {
      // }
      //const maxDate = moment(max).add(dateUnit, 1)
      //let month = parseInt(moment(maxDate).format('M'))

      for (let i = 0; i <= dateNeeded; i++) {
        dateSet.add(
          moment(minDate)
            .add(i, dateUnit)
            .format(format)
        )
      }
      return dateSet
    },
    setData() {
      const dateData = this.findEgdeDates()
      const monthData = dateData.set
      this.interactionByMonth = new Array()
      let interactions = []

      this.format = dateData.format

      if (
        this.agentCoursesData != null &&
        this.agentCoursesData !== undefined
      ) {
        this.agentCoursesData.forEach(element => {
          if (element.interactions != null) {
            element.interactions.forEach(e => {
              interactions.push(Object.assign({}, e))
            })
          }
        })
      } else {
        if (
          this.course.interactions != null &&
          this.course.interactions !== undefined
        ) {
          interactions = Object.assign([], this.course.interactions)
          if (this.agent != null) {
            let a = parseInt(this.agent)
            interactions = _.filter(interactions, function(o) {
              if (o.userId != null && o.userId !== undefined) {
                return o.userId === a
              }
            })
          }
        }
      }

      if (this.selectedOption && this.selectedOption.start) {
        const start = moment(this.selectedOption.start)
        const end = moment(this.selectedOption.end)
        interactions.forEach(element => {
          const time = moment(element.timestamp)
          if (time.isBetween(start, end, this.selectedOption.format, true)) {
            monthData.add(time.format(dateData.format))
          }
        })
      } else {
        interactions.forEach(element => {
          monthData.add(moment(element.timestamp).format(dateData.format))
        })
      }

      for (let [key, value] of monthData) {
        this.interactionByMonth.push({
          month: key,
          count: value - 1
        })
      }
    },
    fillChartData() {
      let dataLabels = new Array()
      let dataSets = new Array()
      let highest = 0
      this.interactionByMonth.forEach(element => {
        dataLabels.push(element.month)
        dataSets.push(element.count)
        highest = element.count > highest ? element.count : highest
      })

      this.experienceOptions = {
        events: ['mousemove', 'click'],
        onHover: (event, chartElement) => {
          event.target.style.cursor = chartElement[0] ? 'pointer' : 'default'
        },
        onClick: this.onClickTimeline,
        legend: {
          display: false
        },
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          yAxes: [
            {
              ticks: {
                min: 0,
                suggestedMin: 50,
                suggestedMax: Math.round(highest + highest / 4),
                stepSize: Math.round(highest / 4)
              },
              gridLines: {
                display: false
              },
              scaleLabel: {
                display: true,
                labelString: 'Interactions'
              }
            }
          ]
        },
        tooltips: {
          backgroundColor: '#FFF',
          titleFontSize: 10,
          titleFontColor: '#000',
          bodyFontColor: '#000',
          bodyFontSize: 12,
          displayColors: false,
          borderWidth: 0.2,
          borderColor: '#000',
          yAlign: 'bottom'
        }
      }

      this.experienceData = {
        labels: dataLabels,
        datasets: [
          {
            fill: true,
            backgroundColor: 'rgba(153, 96, 168, 0.75)',
            data: dataSets
          }
        ]
      }

      //this.dStyle = `width: ${dataLabels.length}px;min-width: 100%`
    },
    onClickTimeline(event, array) {
      if (array.length > 0) {
        const position = array[0]._index
        const dataCount =
          array[0]._chart.tooltip._data.datasets[0].data[position]
        if (dataCount == 0) {
          return
        }
        const label = array[0]._chart.tooltip._data.labels[position]
        this.logData = []
        const courseName =
          this.course != null ? this.course.name.replace(/\s+/g, '_') : 'all'
        this.dialogTitle = timelineTitle
        this.headers = interactionDetailTableHeader
        this.csvFilename = courseName + '_timeline' + '_log'
        this.loading = true
        this.showLog = true
        if (
          this.agentCoursesData != null &&
          this.agentCoursesData !== undefined
        ) {
          this.agentCoursesData.forEach(element => {
            if (element.interactions != null) {
              element.interactions.forEach(e => {
                const time = moment(e.timestamp).format(this.format)

                if (time === label) {
                  this.logData.push(e)
                }
              })
              this.logData = sortDataByDate(this.logData)
            }
          })
        } else {
          let a = this.agent != null ? parseInt(this.agent) : null
          this.$store
            .dispatch('course/fetchCourse', this.course.id)
            .then(response => {
              if (
                response.interactions != null &&
                response.interactions !== undefined
              ) {
                response.interactions.forEach(element => {
                  if (
                    element.duration != null &&
                    element.duration !== undefined
                  ) {
                    element.duration = getDuration(element.duration)
                  }

                  if (element.score != null && element.score !== undefined) {
                    element.score = roundToTwo(element.score)
                  }

                  const time = moment(element.timestamp).format(this.format)

                  if (time === label) {
                    this.logData.push(element)
                  }
                })

                if (a != null) {
                  this.logData = _.filter(this.logData, function(o) {
                    if (o.userId != null && o.userId !== undefined) {
                      return o.userId === a
                    }
                  })
                }
                this.logData = sortDataByDate(this.logData)
                this.loading = false
              }
            })
            .catch(err => (this.loading = false))
        }
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
div.chartWrapper {
  position: relative;
  // overflow-y: hidden;
  overflow: auto;
  width: 100%;
}

div.chartContainer {
  position: relative;
  height: 35vh;
}
/* line-chart */
</style>
